import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import type { HubConnection } from "@microsoft/signalr";
import { EMPTY, Observable, combineLatest, firstValueFrom, from, merge, of, timer } from "rxjs";
import { distinctUntilChanged, filter, map, retry, skip, switchMap, take } from "rxjs/operators";
import { AppSettingsService } from "../../shared/app-settings-service/app-settings.service";
import { AuthService } from "../../shared/auth/auth.service";
import { DashboardsService } from "../../shared/dashboards-service/dashboards.service";
import { ResponseErrorHandlerService } from "../../shared/response-error-handler-service/response-error-handler.service";
import { NegotiateResponse } from "./negotiate-response.interface";

const RETRY_FAST_DELAY_MS = 5_000;
const RETRY_SLOW_DELAY_MS = 60_000;
const RETRY_FAST_COUNT = 3;

@Injectable({
    providedIn: "root",
})
export class SignalRService {
    private readonly http = inject(HttpClient);
    private readonly appSettingsService = inject(AppSettingsService);
    private readonly authService = inject(AuthService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);

    connection: HubConnection | null = null;
    readonly signalRConnected = signal(false);
    firstToken: string | null;

    constructor() {
        const dashboardId$ = merge(
            // Bootstrap: resolve current dashboard when session is ready
            this.authService.sessionData$.pipe(
                filter((initialized) => initialized !== null),
                take(1),
                switchMap(() => from(this.dashboardsService.getSafeCurrentDashboardId())),
            ),
            // React to the user dashboard changed
            this.dashboardsService.currentDashboardChanged$.pipe(filter((id) => !!id)),
            // Disconnect on logout user switching dashboards
            this.dashboardsService.dashboardChangeEvent$.pipe(map(() => null as string | null)),
            // Disconnect on relogin prompted (false) and restart once relogged (true)
            this.authService.reloginPrompted$.pipe(
                switchMap((reloginPrompted) => {
                    if (reloginPrompted) {
                        return of(null);
                    }
                    return from(this.dashboardsService.getSafeCurrentDashboardId());
                }),
            ),
        );

        combineLatest([dashboardId$, this.authService.reloginPrompted$])
            .pipe(
                // While the host shows its relogin dialog, the session this app authenticates with
                // is dead: negotiate would 401 and the Hub would drop us anyway. Drop the target to
                // tear the connection down (and to cancel any in-flight 401 retry wait), then restore
                // it once "sessionChanged" clears the flag -- by which point AuthService has already
                // published the new session name, so the re-negotiate picks it up.
                map(([id, inReloginPrompted]) => (inReloginPrompted ? null : id)),
                distinctUntilChanged(),
                switchMap((id) => (id ? this.connectToHub(id) : EMPTY)),
                takeUntilDestroyed(),
            )
            .subscribe((conn) => {
                this.connection = conn;
                this.signalRConnected.set(true);
                this.responseErrorHandlerService.hideBannerEmitter.next({ type: "signalR" });
            });
    }

    private connectToHub(dashboardId: string): Observable<HubConnection> {
        return new Observable<HubConnection>((subscriber) => {
            let conn: HubConnection | null = null;
            let cancelled = false;

            const start = async () => {
                // The client is only needed once a connection is actually being established, which is
                // never during first paint, so it is imported on demand to stay out of the initial bundle.
                const [{ HubConnectionBuilder, LogLevel }, { url, accessToken: initialToken }] = await Promise.all([
                    import("@microsoft/signalr"),
                    firstValueFrom(this.negotiate(dashboardId)),
                ]);
                if (cancelled) return;

                // Use the initial token for the first connect; re-negotiate for subsequent reconnects
                this.firstToken = initialToken;

                conn = new HubConnectionBuilder()
                    .withUrl(url, {
                        // The SignalR client's own internal negotiate/HTTP-transport requests bypass
                        // Angular's HttpClient (and therefore AuthInterceptor), so they never get the
                        // "x-site"/"x-userid"/"x-session-name" headers that every other API call gets.
                        // Attach them the same way AuthService.addSingleSessionHeader() does for
                        // AuthInterceptor, so this app's auth pipeline can authenticate these requests
                        // too (needed for the self-hosted local dev Hub; harmless against Azure SignalR).
                        headers: this.buildSessionHeaders(),
                        accessTokenFactory: async () => {
                            if (this.firstToken !== null) {
                                return this.firstToken;
                            }

                            const { accessToken } = await firstValueFrom(this.negotiate(dashboardId));
                            this.firstToken = accessToken;
                            return accessToken;
                        },
                    })
                    .withAutomaticReconnect()
                    .configureLogging(LogLevel.Information)
                    .build();

                conn.onreconnecting(() => {
                    this.firstToken = null;
                    this.signalRConnected.set(false);
                    this.responseErrorHandlerService.showBannerEmitter.next({ type: "signalR" });
                });

                conn.onreconnected(() => {
                    if (this.connection === conn) {
                        this.signalRConnected.set(true);
                        this.responseErrorHandlerService.hideBannerEmitter.next({ type: "signalR" });
                    }
                });

                // onclose fires on manual stop (teardown) or when withAutomaticReconnect exhausts all retries
                conn.onclose((err) => {
                    if (this.connection === conn) {
                        this.connection = null;
                        this.signalRConnected.set(false);
                    }
                    // If not torn down intentionally by switchMap, propagate error to trigger retry
                    if (!subscriber.closed) {
                        console.error("Real-time updates: Connection lost. Scheduling reconnect.", err);
                        subscriber.error(err ?? new Error("Connection closed unexpectedly"));
                    }
                });

                await conn.start();
                if (cancelled) {
                    conn.stop();
                    return;
                }

                subscriber.next(conn);
            };

            start().catch((err) => {
                if (!subscriber.closed) subscriber.error(err);
            });

            return () => {
                cancelled = true;
                conn?.stop();
            };
        }).pipe(
            retry({
                delay: (err, retryCount) => {
                    // A 401 means the session headers this app authenticates with were rejected.
                    // Retrying on a timer would just re-present the same rejected session, so wait
                    // for the session to actually change -- a keepalive updateSession or a completed
                    // relogin -- and make exactly one attempt per change. skip(1) discards the
                    // signal's current value, which is the session that was just refused.
                    if (err instanceof HttpErrorResponse && err.status === 401) {
                        // The wait lasts until the next refresh, so it can span most of a session
                        // window. Surface the banner rather than let updates go silently stale.
                        this.responseErrorHandlerService.showBannerEmitter.next({ type: "signalR" });
                        return this.authService.sessionData$.pipe(skip(1), take(1));
                    }
                    if (retryCount % RETRY_FAST_COUNT === 0) {
                        this.responseErrorHandlerService.showBannerEmitter.next({ type: "signalR" });
                        return timer(RETRY_SLOW_DELAY_MS);
                    }
                    return timer(RETRY_FAST_DELAY_MS);
                },
                resetOnSuccess: true,
            }),
        );
    }

    private negotiate(dashboardId: string) {
        return this.http.get<NegotiateResponse>(this.buildNegotiateUrl(dashboardId), {
            headers: new HttpHeaders().set("showBanner", "false"),
        });
    }

    /** Mirrors AuthService.addSingleSessionHeader() for requests that don't go through AuthInterceptor. */
    private buildSessionHeaders(): Record<string, string> {
        const session = this.authService.sessionData();
        if (!session || !session.site || !session.userId || !session.sessionName) {
            return {};
        }
        return {
            "x-site": session.site,
            "x-userid": session.userId,
            "x-session-name": session.sessionName,
            "Referrer-Policy": "strict-origin-when-cross-origin",
            "Permissions-Policy":
                "geolocation=(), midi=(), notifications=(), push=(), sync-xhr=(), microphone=(), camera=(), magnetometer=(), gyroscope=(), speaker=(), vibrate=(), fullscreen=(), payment=()",
        };
    }

    private buildNegotiateUrl(dashboardId: string): string {
        return (
            this.appSettingsService.getSetting("SignalRService_URL") +
            this.appSettingsService.getSetting("SignalRService_Version") +
            "negotiate/" +
            dashboardId
        );
    }
}
